import { Component, OnDestroy, OnInit } from '@angular/core';
import { AdvancedForm } from "@magieno/angular-advanced-forms";
import {
  ClientQuestionnaireCreationOptions,
} from '../../../../../../../../common/src/options/client-questionnaire/client-questionnaire-creation.options';
import { CreateClientQuestionnaireFactory } from './create-client-questionnaire.factory';
import { UntypedFormControl } from '@angular/forms';
import {
  ClientQuestionnaireKeynameEnum,
} from '../../../../../../../../common/src/enums/client-questionnaire/client-questionnaire-keyname.enum';
import { FormLifecycleStatusEnum } from '@magieno/angular-advanced-forms';
import { AdvancedFormButton } from "@magieno/angular-advanced-forms";
import { AdvancedFormControl } from "@magieno/angular-advanced-forms";
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClientQuestionnaireStore,
} from '../../../../../../modules/src/lib/questionnaires/stores/client-questionnaire.store';
import { AlertMessageStore } from '../../../../../../modules/src/lib/core/stores/alert-message.store';
import { AlertMessageModel } from '../../../../../../modules/src/lib/core/models/alert-message.model';
import { SeverityTypeEnum } from '../../../../../../modules/src/lib/core/enums/severity-type.enum';
import { AlertContextEnum } from '../../../../../../modules/src/lib/core/enums/alert-context.enum';
import { FlagManager } from '../../../../../../modules/src/lib/core/managers/flag.manager';
import { FlagEnum } from '../../../../../../modules/src/lib/core/enums/flag.enum';
import { LoggingService } from '../../../../../../modules/src/lib/core/services/logging.service';

@Component({
  selector: 'app-list-client-questionnaires',
  templateUrl: './create-client-questionnaire.component.html',
  styleUrls: ['./create-client-questionnaire.component.scss'],
  standalone: false,
})
export class CreateClientQuestionnaireComponent implements OnInit, OnDestroy {
  public readonly form: AdvancedForm<ClientQuestionnaireCreationOptions> = this.formFactory.create();

  clientQuestionnaireDetails: { name: string, value: string, title: string, text: string }[] = [];

  subscriptions: Subscription[] = [];

  constructor(private readonly formFactory: CreateClientQuestionnaireFactory,
              private readonly store: ClientQuestionnaireStore,
              private readonly alertMessageStore: AlertMessageStore,
              private readonly router: Router,
              private readonly loggingService: LoggingService,
              private readonly flagManager: FlagManager,
              private readonly route: ActivatedRoute) {
    this.clientQuestionnaireDetails = [];

    if (this.flagManager.isActive(FlagEnum.ClientQuestionnairesQuebec2022TaxForm)) {
      this.clientQuestionnaireDetails.push({
        name: 'clientQuestionnaireKeynameEnum',
        value: ClientQuestionnaireKeynameEnum.QuebecTaxForm2022,
        title: '2022 - Formulaire de collecte de données Québec Impôts pour l\'année 2022',
        text: 'Ce formulaire permettra à votre client de vous transmettre toutes ses informations reliées à la période d\'impôt 2022. Ce formulaire collecte les informations pour la déclaration d\'impôt fédéral et du Québec.',
      });
    }

    if (this.flagManager.isActive(FlagEnum.ClientQuestionnairesQuebec2023TaxForm)) {
      this.clientQuestionnaireDetails.push({
        name: 'clientQuestionnaireKeynameEnum',
        value: ClientQuestionnaireKeynameEnum.QuebecTaxForm2023,
        title: '2023 - Formulaire de collecte de données Québec Impôts pour l\'année 2023',
        text: 'Ce formulaire permettra à votre client de vous transmettre toutes ses informations reliées à la période d\'impôt 2023. Ce formulaire collecte les informations pour la déclaration d\'impôt fédéral et du Québec.',
      });
    }

    if (this.flagManager.isActive(FlagEnum.SalaryDividendTaxForm)) {
      this.clientQuestionnaireDetails.push({
        name: 'clientQuestionnaireKeynameEnum',
        value: ClientQuestionnaireKeynameEnum.SalaryDividend,
        title: 'Formulaire de collecte de données pour l\'analyse Salaires-Dividendes',
        text: 'Ce formulaire vous permet de collecter les informations requises afin d\'effectuer l\'analyse salaire-dividende.',
      });
    }
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }


  ngOnInit(): void {
    // Right away, we want to form to be set as loading.
    this.form.lifecycleStatus = FormLifecycleStatusEnum.Loading;

    // This following method registers an observable on any button clicked in the form. Each button has an id
    // and that's how we know which button was clicked on.
    this.subscriptions.push(this.form.buttonClick$.subscribe(async (button: AdvancedFormButton) => {
      switch (button.id) {
        case 'CREATE':
          await this.create();
          break;
      }
    }));

    this.form.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;

    // Set default value.
    (this.form.formElements.keyname as AdvancedFormControl<ClientQuestionnaireKeynameEnum, ClientQuestionnaireKeynameEnum, ClientQuestionnaireKeynameEnum>).setNormalizedValue(ClientQuestionnaireKeynameEnum.QuebecTaxForm2023);

    this.subscriptions.push(this.route.queryParams.subscribe((params) => {
      if (params.default && Object.values(ClientQuestionnaireKeynameEnum).includes(params.default)) {
        (this.form.formElements.keyname as AdvancedFormControl<ClientQuestionnaireKeynameEnum, ClientQuestionnaireKeynameEnum, ClientQuestionnaireKeynameEnum>).setNormalizedValue(params.default);
      }
    }));


  }

  async create() {
    try {
      const validationErrors = await this.form.validate();
      if (validationErrors && validationErrors.length > 0) {
        this.form.setButtonLifecycleStatus(FormLifecycleStatusEnum.ReadyForInput);
        this.form.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;
        return;
      }

      const clientQuestionnaireKeynameEnum = this.form.value.keyname;
      if (!clientQuestionnaireKeynameEnum) {
        this.loggingService.error('The Client Questionnaire Form Keyname enum is not defined. Can not continue');
        return;
      }

      this.form.lifecycleStatus = FormLifecycleStatusEnum.Loading;

      const options = this.form.value;

      const modelSubscription = await this.store.create(options);

      this.subscriptions.push(modelSubscription.subscribe(async model => {
        this.form.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;

        switch (clientQuestionnaireKeynameEnum) {
          case ClientQuestionnaireKeynameEnum.QuebecTaxForm2023:
            return await this.router.navigate([ClientQuestionnaireKeynameEnum.QuebecTaxForm2023, model.id, 'notify'], {
              relativeTo: this.route.parent,
            });
          case ClientQuestionnaireKeynameEnum.QuebecTaxForm2022:
            return await this.router.navigate([ClientQuestionnaireKeynameEnum.QuebecTaxForm2022, model.id, 'notify'], {
              relativeTo: this.route.parent,
            });
          default:
            this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Error, AlertContextEnum.Global, {
              fr: 'Ce formulaire ne peut être créé présentement',
              en: 'This form cannot be created at the moment',
            }));
            this.loggingService.error('Error clientQuestionnaireKeyname ' + clientQuestionnaireKeynameEnum + ' unknown');
            break;
        }

      }));
    } catch (e) {
      this.form.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;
    }
  }

  getClientNameFormControl(): UntypedFormControl {
    return this.form.formElements.clientName.control as UntypedFormControl;
  }

  getClientEmailFormControl(): UntypedFormControl {
    return this.form.formElements.clientEmail.control as UntypedFormControl;
  }

  getKeynameEnumFormControl(): UntypedFormControl {
    return this.form.formElements.keyname.control as UntypedFormControl;
  }

  private unsubscribeAll() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }


}
